

import React, { useState, useEffect } from 'react';
import { Project, ProjectWithZones } from '@/types';
import { Input } from '@/shared/ui/input';
import { Textarea } from '@/shared/ui/textarea';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { BuildingType } from '@/features/projects/types';

/**
 * 基本情報セクションのプロパティ
 */
interface BasicInfoSectionProps {
  project: ProjectWithZones;
  isReadOnly: boolean;
  onChange: (_updatedInfo: Partial<Project>) => void;
}

/**
 * 基本情報セクション
 * @param project プロジェクトデータ
 * @param isReadOnly 読み取り専用かどうか
 * @param onChange 変更時のコールバック
 */
export const BasicInfoSection: React.FC<BasicInfoSectionProps> = ({
  project,
  isReadOnly,
  onChange,
}) => {
  // 編集用の内部状態
  const [name, setName] = useState(project.name || '');
  const [buildingType, setBuildingType] = useState(project.building_type || '');
  const [address, setAddress] = useState(project.address || '');
  const [notes, setNotes] = useState(project.notes || '');

  // 変更時にonChangeコールバックを呼び出す
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    onChange({ name: value });
  };

  /**
   * 建築種別の変更時のコールバック
   * @param value 選択された値
   */
  const handleBuildingTypeChange = (value: string) => {
    setBuildingType(value);
    onChange({ building_type: value as BuildingType });
  };

  /**
   * 住所の変更時のコールバック
   * @param e イベント
   */
  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAddress(value);
    onChange({ address: value });
  };

  /**
   * 特記事項の変更時のコールバック
   * @param e イベント
   */
  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNotes(value);
    onChange({ notes: value });
  };

  // プロジェクトデータが変更された場合、内部状態を更新
  useEffect(() => {
    setName(project.name || '');
    setBuildingType(project.building_type || '');
    setAddress(project.address || '');
    setNotes(project.notes || '');
  }, [project]);

  // 表示モード（読み取り専用）
  if (isReadOnly) {
    return (
      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <Label>プロジェクト名</Label>
          <div className="mt-1 p-2 bg-muted/10 rounded-md">{project.name || '-'}</div>
        </div>

        <div>
          <Label>建築種別</Label>
          <div className="mt-1 p-2 bg-muted/10 rounded-md">
            {project.building_type || '-'}
          </div>
        </div>

        <div>
          <Label>住所</Label>
          <div className="mt-1 p-2 bg-muted/10 rounded-md">{project.address || '-'}</div>
        </div>

        <div>
          <Label>特記事項</Label>
          <div className="mt-1 p-2 bg-muted/10 rounded-md">{project.notes || '-'}</div>
        </div>
      </div>
    );
  }

  // 編集モード
  return (
    <div className="grid gap-3 md:grid-cols-2">
      <div>
        <Label htmlFor="project-name">プロジェクト名</Label>
        <Input
          id="project-name"
          value={name}
          onChange={handleNameChange}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="building-type">建築種別</Label>
        <Select value={buildingType} onValueChange={handleBuildingTypeChange}>
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="建築種別を選択してください" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="商業施設">商業施設</SelectItem>
            <SelectItem value="住宅">住宅</SelectItem>
            <SelectItem value="オフィス">オフィス</SelectItem>
            <SelectItem value="分譲マンション">分譲マンション</SelectItem>
            <SelectItem value="戸建住宅">戸建住宅</SelectItem>
            <SelectItem value="工場・倉庫">工場・倉庫</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label htmlFor="address">住所</Label>
        <Input
          id="address"
          value={address}
          onChange={handleAddressChange}
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="notes">特記事項</Label>
        <Textarea
          id="notes"
          value={notes}
          onChange={handleNotesChange}
          className="mt-1"
        />
      </div>
    </div>
  );
}; 